import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { ShoppingBag, User, LogOut, Menu, X, Search } from "lucide-react";
import { useState } from "react";

export const Header = () => {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const submitSearch = (e) => {
    e?.preventDefault?.();
    const q = searchText.trim();
    setSearchOpen(false);
    setSearchText("");
    navigate(q ? `/collection?q=${encodeURIComponent(q)}` : "/collection");
  };

  const navLink = ({ isActive }) =>
    `u-link text-sm tracking-[0.18em] uppercase ${
      isActive ? "text-[#c58b76]" : "text-[#1a1918]"
    }`;

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-8">
        <button
          className="md:hidden"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 mr-auto md:mr-0">
          <div className="font-serif text-2xl tracking-tight">konokangen</div>
          <div className="w-1.5 h-1.5 rounded-full bg-[#c58b76]"></div>
        </Link>

        <nav className="hidden md:flex items-center gap-9 mx-auto">
          <NavLink to="/" end className={navLink} data-testid="nav-home">Atelier</NavLink>
          <NavLink to="/collection" className={navLink} data-testid="nav-collection">Collection</NavLink>
          <NavLink to="/collection/rings" className={navLink} data-testid="nav-rings">Rings</NavLink>
          <NavLink to="/collection/necklaces" className={navLink} data-testid="nav-necklaces">Necklaces</NavLink>
          <NavLink to="/about" className={navLink} data-testid="nav-about">Our Story</NavLink>
        </nav>

        <div className="flex items-center gap-5">
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            className="text-[#1a1918] hover:text-[#c58b76]"
            data-testid="header-search-toggle"
            aria-label="Search"
          >
            <Search size={18} />
          </button>
          {user ? (
            <div className="hidden md:flex items-center gap-4">
              <span className="text-xs tracking-widest uppercase text-[#5c5a56]" data-testid="user-name">
                {user.name?.split(" ")[0]}
              </span>
              {user.role === "admin" && (
                <Link to="/admin" className="overline" data-testid="link-admin">Admin</Link>
              )}
              {(user.role === "support" || user.role === "admin") && (
                <Link to="/support" className="overline" data-testid="link-support">Support</Link>
              )}
              <button
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className="text-[#1a1918] hover:text-[#c58b76]"
                data-testid="logout-btn"
                aria-label="Logout"
              >
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              className="hidden md:flex items-center text-[#1a1918] hover:text-[#c58b76]"
              data-testid="login-link"
              aria-label="Login"
            >
              <User size={18} />
            </Link>
          )}
          <Link to="/cart" className="relative flex items-center" data-testid="cart-link" aria-label="Cart">
            <ShoppingBag size={20} className="text-[#1a1918]" />
            {count > 0 && (
              <span
                className="absolute -top-2 -right-3 bg-[#c58b76] text-white text-[10px] font-medium w-5 h-5 flex items-center justify-center rounded-full"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </Link>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-[#ebe5df] bg-white" data-testid="header-search-panel">
          <form onSubmit={submitSearch} className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center gap-3">
            <Search size={18} className="text-[#5c5a56]" />
            <input
              autoFocus
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search rings, necklaces, materials, styles…"
              className="flex-1 bg-transparent border-0 focus:outline-none text-base"
              data-testid="header-search-input"
            />
            <button
              type="submit"
              className="overline text-[#c58b76] hover:text-[#1a1918]"
              data-testid="header-search-submit"
            >
              Search →
            </button>
            <button
              type="button"
              onClick={() => setSearchOpen(false)}
              className="text-[#5c5a56] hover:text-[#1a1918]"
              data-testid="header-search-close"
            >
              <X size={18} />
            </button>
          </form>
        </div>
      )}

      {open && (
        <div className="md:hidden border-t border-[#ebe5df] bg-white">
          <div className="flex flex-col p-6 gap-4">
            {[
              ["/", "Atelier"],
              ["/collection", "Collection"],
              ["/collection/rings", "Rings"],
              ["/collection/necklaces", "Necklaces"],
              ["/about", "Our Story"],
            ].map(([to, label]) => (
              <Link
                key={to}
                to={to}
                className="text-sm uppercase tracking-[0.18em]"
                onClick={() => setOpen(false)}
              >
                {label}
              </Link>
            ))}
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="overline">Admin</Link>}
                {(user.role === "support" || user.role === "admin") && (
                  <Link to="/support" onClick={() => setOpen(false)} className="overline">Support</Link>
                )}
                <button
                  onClick={() => {
                    logout();
                    setOpen(false);
                    navigate("/");
                  }}
                  className="text-left text-sm uppercase tracking-[0.18em]"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="text-sm uppercase tracking-[0.18em]">
                Sign in
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  const { content } = useSiteContent();
  const tagline =
    content?.footer_tagline ||
    "Contemporary Indian-fusion fine jewellery. Every piece sculpted by hand in Jaipur, designed for the women who write their own stories.";
  return (
  <footer className="relative z-10 bg-[#1a1918] text-[#fdfbf7] mt-32">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-4 gap-12">
      <div className="md:col-span-2">
        <div className="font-serif text-4xl tracking-tight mb-4">konokangen</div>
        <p className="text-sm text-[#a8a39d] leading-relaxed max-w-md">{tagline}</p>
        <div className="mt-8 overline text-[#c58b76]">Hallmarked · BIS Certified · Free PAN-India shipping</div>
      </div>
      <div>
        <div className="overline mb-4 text-[#c58b76]">Atelier</div>
        <ul className="space-y-3 text-sm text-[#d6d2cc]">
          <li><Link to="/collection/rings" className="u-link">Rings</Link></li>
          <li><Link to="/collection/necklaces" className="u-link">Necklaces</Link></li>
          <li><Link to="/collection/earrings" className="u-link">Earrings</Link></li>
          <li><Link to="/collection/bracelets" className="u-link">Bracelets</Link></li>
        </ul>
      </div>
      <div>
        <div className="overline mb-4 text-[#c58b76]">House</div>
        <ul className="space-y-3 text-sm text-[#d6d2cc]">
          <li><Link to="/about" className="u-link">Our Story</Link></li>
          <li><a className="u-link" href="#">Care Guide</a></li>
          <li><a className="u-link" href="#">Shipping & Returns</a></li>
          <li><a className="u-link" href="#">Contact</a></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-[#2b2826]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex justify-between items-center text-xs text-[#807c75]">
        <span>© {new Date().getFullYear()} konokangen.com · Made with intention.</span>
        <span className="overline">est. mmxxvi</span>
      </div>
    </div>
  </footer>
  );
};
